/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { fetchCustomerAccounts } from '../infra/customerRepository';
import type { CustomerAccountRow } from '../domain/customerTypes';

export type CustomerSearchSelection = {
  id: string;
  name: string;
  phone: string | null;
};

type Props = {
  selected: CustomerSearchSelection | null;
  onSelect: (customer: CustomerSearchSelection) => void;
  onClear: () => void;
  /** Free-text name when no customer linked (walk-in). Controlled by parent. */
  freeTextName?: string;
  onFreeTextNameChange?: (name: string) => void;
  allowFreeText?: boolean;
  testID?: string;
  /** Exact testID on the TextInput (defaults to `${testID}.input`). */
  inputTestID?: string;
};

export function CustomerSearchField({
  selected,
  onSelect,
  onClear,
  freeTextName = '',
  onFreeTextNameChange,
  allowFreeText = true,
  testID = 'customer-search',
  inputTestID,
}: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CustomerAccountRow[]>([]);
  const [searching, setSearching] = useState(false);

  const searchText = allowFreeText ? freeTextName : query;

  useEffect(() => {
    if (selected) return;
    const q = searchText.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const rows = await fetchCustomerAccounts({
            limit: 12,
            query: { search: q, sortBy: 'name_asc' },
          });
          setResults(rows);
        } catch {
          setResults([]);
        } finally {
          setSearching(false);
        }
      })();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, selected]);

  if (selected) {
    return (
      <View style={styles.selectedCard} testID={`${testID}.selected`}>
        <View style={styles.selectedInfo}>
          <Text style={styles.selectedName}>{selected.name}</Text>
          {selected.phone ? <Text style={styles.selectedMeta}>{selected.phone}</Text> : null}
        </View>
        <TouchableOpacity onPress={onClear} style={styles.clearBtn} testID={`${testID}.clear`}>
          <Text style={styles.clearText}>Quitar</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View>
      <TextInput
        style={styles.input}
        value={searchText}
        onChangeText={(v) => {
          if (allowFreeText) onFreeTextNameChange?.(v);
          else setQuery(v);
        }}
        placeholder="Buscar cliente por nombre o teléfono…"
        placeholderTextColor={colors.inputPlaceholder}
        autoCorrect={false}
        testID={inputTestID ?? `${testID}.input`}
      />
      {searching ? (
        <ActivityIndicator size="small" color={colors.accent} style={styles.spinner} />
      ) : null}
      {results.length > 0 ? (
        <View style={styles.results} testID={`${testID}.results`}>
          {results.map((c) => (
            <TouchableOpacity
              key={c.id}
              style={styles.option}
              onPress={() => {
                onSelect({ id: c.id, name: c.name, phone: c.phone || null });
                setQuery('');
                setResults([]);
              }}
              testID={`${testID}.option`}
            >
              <Text style={styles.optionName}>{c.name}</Text>
              {c.phone ? <Text style={styles.optionMeta}>{c.phone}</Text> : null}
            </TouchableOpacity>
          ))}
        </View>
      ) : null}
      {searchText.trim().length >= 2 && !searching && results.length === 0 ? (
        <Text style={styles.noResults}>
          {allowFreeText
            ? 'Sin coincidencias — se guarda el nombre escrito.'
            : 'No se encontraron clientes.'}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  input: {
    backgroundColor: colors.inputBackground,
    borderWidth: 1,
    borderColor: colors.inputBorder,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    fontSize: 15,
    color: colors.textPrimary,
  },
  spinner: { marginTop: spacing.xs },
  results: {
    marginTop: spacing.xs,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    overflow: 'hidden',
  },
  option: {
    paddingVertical: spacing.sm + 2,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderSoft,
  },
  optionName: { ...typography.body, color: colors.textPrimary, fontWeight: '500' },
  optionMeta: { ...typography.caption, color: colors.textSecondary, marginTop: 2 },
  noResults: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: spacing.xs,
    fontStyle: 'italic',
  },
  selectedCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.accentSoft,
    borderWidth: 1,
    borderColor: colors.accent,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
  },
  selectedInfo: { flex: 1 },
  selectedName: { ...typography.body, color: colors.accentStrong, fontWeight: '600' },
  selectedMeta: { ...typography.caption, color: colors.accent, marginTop: 2 },
  clearBtn: { paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  clearText: { color: colors.danger, fontSize: 13, fontWeight: '600' },
});
